import { Alert, Button, FormValues, Typography, SkeletonLoader, useAppForm } from '@stern-ui/ui';
import { useStore } from '@tanstack/react-form';
import { LockOpenOutline } from 'mdi-material-ui';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import z from 'zod';

import { DialogContainer } from '@/components';
import { isUs, optOutOptions, phoneNumberPrefix, phoneRegex } from '@/globalConsts';
import { useDisclosure } from '@/hooks';

import { formatPhoneNumberDisplay } from '../../utils';

type EditPatientInformationFormProps = {
	isLoading: boolean;
	email: string;
	mobile_number: string;
	home_number: string;
	needs_assistance: string;
	setShowEdit?: React.Dispatch<React.SetStateAction<boolean>>;
	submitAction: (values: FormValues) => void;
};

const emailOrEmpty = z.email('Email address must be a valid email').or(z.literal(''));

const phoneOrEmpty = z
	.string()
	.regex(phoneRegex, 'Phone number format is invalid')
	.or(z.literal(''));

const checkboxStringToBool = z.enum(['true', 'false']).transform((v) => v === 'true');

const editPatientInformationSchema = z
	.object({
		contact_email: emailOrEmpty, // '' allowed
		contact_mobile_number: phoneOrEmpty, // '' allowed
		contact_home_number: phoneOrEmpty,
		needs_assistance: z.string(),
		opt_out: checkboxStringToBool,
		opt_out_reason: z.string().optional(),
		opt_out_reason_other: z.string().optional(),
		deceased: checkboxStringToBool,
	})
	.superRefine((data, ctx) => {
		if (!data.contact_email && !data.contact_home_number && !data.contact_mobile_number) {
			ctx.addIssue({
				code: 'custom',
				message: 'At least one contact method is required',
				path: ['contact_email'],
			});
		}

		if (data.opt_out) {
			if (!data.opt_out_reason) {
				ctx.addIssue({
					code: 'custom',
					message: 'Opt out reason is required',
					path: ['opt_out_reason'],
				});
			}
		}

		if (data.opt_out_reason === 'other' && !data.opt_out_reason_other) {
			ctx.addIssue({
				code: 'custom',
				message: 'Other reason for opting out is required',
				path: ['opt_out_reason_other'],
			});
		}
	});

export const EditPatientInformationForm = ({
	isLoading,
	email,
	mobile_number,
	home_number,
	needs_assistance,
	submitAction,
	setShowEdit,
}: EditPatientInformationFormProps) => {
	const navigate = useNavigate();
	const { close, isOpen, open } = useDisclosure();
	const [dialogCancel, setDialogCancel] = useState(false);

	type EditPatientInformation = z.input<typeof editPatientInformationSchema>;

	const defaultValues: EditPatientInformation = {
		contact_email: email || '',
		contact_mobile_number: formatPhoneNumberDisplay(mobile_number) || '',
		contact_home_number: formatPhoneNumberDisplay(home_number) || '',
		needs_assistance: `${needs_assistance}`,
		opt_out: 'false',
		opt_out_reason: '',
		opt_out_reason_other: '',
		deceased: 'false',
	};

	const form = useAppForm({
		defaultValues: defaultValues,
		validators: {
			onMount: editPatientInformationSchema,
			onChange: editPatientInformationSchema,
		},
		onSubmit: ({ value }) => submitAction(value),
	});

	const optOut = useStore(form.store, (state) => state.values.opt_out); // 'true' | 'false'
	const optOutReasonVal = useStore(form.store, (state) => state.values.opt_out_reason);
	const optOutReasonOtherVal = useStore(form.store, (state) => state.values.opt_out_reason_other);

	const isOptOutChecked = optOut === 'true';
	const isOptOutReasonOther = optOutReasonVal === 'other';
	const isDeceasedChecked = useStore(form.store, (state) => state.values.deceased) === 'true';
	const isOptoutChecked = optOut === 'true';

	useEffect(() => {
		if (optOut === 'false') {
			if (optOutReasonVal !== '') form.setFieldValue('opt_out_reason', '');
			if (optOutReasonOtherVal !== '') form.setFieldValue('opt_out_reason_other', '');
		}
	}, [optOut, optOutReasonVal, optOutReasonOtherVal, form]);

	useEffect(() => {
		if (optOutReasonVal !== 'other') {
			if (optOutReasonOtherVal !== '') form.setFieldValue('opt_out_reason_other', '');
		}
	}, [optOutReasonVal, optOutReasonOtherVal, form]);

	const handleCancel = () => {
		if (setShowEdit) {
			// close the edit form
			setShowEdit(false);
		} else {
			// redirect back to patient details
			navigate('../', { relative: 'path' });
		}
	};

	const textFieldIcon = (
		<LockOpenOutline className='ml-2 mb-1 text-primaryTextDark' fontSize='small' />
	);

	return (
		<form
			onSubmit={(e) => {
				e.preventDefault();
				void form.handleSubmit();
			}}
		>
			<div data-test-id='edit-patient-information-form'>
				<Typography variant='heading_4_medium' className='text-primaryTextDark mb-6'>
					Update patient information
				</Typography>
				<div className='bg-infoPanelBackground p-4 rounded-md mb-6'>
					<Typography variant='body_lg_medium' className='text-primaryTextDark mb-6'>
						Personal information
					</Typography>
					<div className='grid grid-cols-2 grid-rows-2 items-start gap-x-10 gap-y-6 w-full'>
						{isLoading ? (
							<>
								<SkeletonLoader className='h-16 w-full rounded-sm' />
								<SkeletonLoader className='h-16 w-full rounded-sm' />
								<SkeletonLoader className='h-16 w-full rounded-sm' />
							</>
						) : (
							<>
								<div className='flex flex-col'>
									<form.AppField
										name='contact_email'
										children={(field) => (
											<field.TextField
												label='Email'
												type='email'
												icon={textFieldIcon}
											/>
										)}
									/>
								</div>
								<div className='flex flex-col'>
									<form.AppField
										name='contact_mobile_number'
										children={(field) => (
											<field.PhoneNumberFieldV2
												label='Mobile number'
												isUs={isUs}
												phoneNumberPrefix={phoneNumberPrefix}
												icon={textFieldIcon}
											/>
										)}
									/>
								</div>
								<div className='flex flex-col'>
									<form.AppField
										name='contact_home_number'
										children={(field) => (
											<field.PhoneNumberFieldV2
												label='Home number'
												isUs={isUs}
												phoneNumberPrefix={phoneNumberPrefix}
												icon={textFieldIcon}
											/>
										)}
									/>
								</div>
							</>
						)}
					</div>
				</div>
				<div className='bg-tertiaryBackground p-4 rounded-md border-secondaryBorder border mb-6'>
					<div className='w-full grid grid-cols gap-4 md:gap-8'>
						{isLoading ? (
							<>
								<SkeletonLoader className='h-16 w-full rounded-sm' />
							</>
						) : (
							<form.AppField
								name='needs_assistance'
								children={(field) => (
									<field.RadioField
										groupLabel='Does patient need assistance?'
										options={[
											{
												name: 'needs_assistance',
												label: 'Yes',
												value: 'true',
											},
											{
												name: 'needs_assistance',
												label: 'No',
												value: 'false',
											},
										]}
										dataTestId='needs-assistance-group'
									/>
								)}
							/>
						)}
					</div>
				</div>
				<Alert severity='info' title='Information' className=' mb-6'>
					A patient can only have one of the following statuses assigned: Opt Out or
					Deceased.
				</Alert>
				<div className='bg-tertiaryBackground p-4 rounded-md border-secondaryBorder border mb-6'>
					<div className={`w-full ${isDeceasedChecked ? 'opacity-40' : ''}`}>
						<Typography variant='body_lg_medium' className='text-primaryTextDark'>
							Opt the patient out
						</Typography>
						<Typography
							variant='body_md_regular'
							className='text-tertiaryTextDark mt-0.5 mb-6'
						>
							Opting a patient out means they will no longer be contacted about future
							research opportunities and will be removed from all studies.
						</Typography>
						<form.AppField
							name='opt_out'
							children={(field) => <field.CheckboxFieldV2 label='Opt patient out' />}
						/>
						<div className='mt-6 max-w-96'>
							<form.AppField
								name='opt_out_reason'
								children={(field) => (
									<field.SelectField
										label='Reason for opt-out'
										options={optOutOptions}
										isDisabled={!isOptOutChecked}
									/>
								)}
							/>
						</div>
						{isOptOutReasonOther && (
							<div className='mt-8 max-w-lg'>
								<form.AppField
									name='opt_out_reason_other'
									children={(field) => (
										<field.TextAreaField
											label='Additional details'
											name='opt_out_reason_other'
										/>
									)}
								/>
							</div>
						)}
					</div>
				</div>
				<div className='bg-tertiaryBackground p-4 rounded-md border-secondaryBorder border mb-6'>
					<div className={`w-full ${isOptoutChecked ? 'opacity-40' : ''}`}>
						<Typography variant='body_lg_medium' className='text-primaryTextDark'>
							Mark the patient as deceased
						</Typography>
						<Typography
							variant='body_md_regular'
							className='text-tertiaryTextDark mt-0.5 mb-6'
						>
							Marking a patient as deceased will remove them from all studies and stop
							any future contact.
						</Typography>
						<form.AppField
							name='deceased'
							children={(field) => (
								<field.CheckboxFieldV2 label='Confirm patient is deceased' />
							)}
						/>
					</div>
				</div>
				<div className='flex flex-row justify-between pb-8'>
					<Button
						type='button'
						variant='tertiary'
						onClick={() => {
							setDialogCancel(true);
							open();
						}}
					>
						Cancel
					</Button>
					<div className='flex flex-row'>
						<form.Subscribe
							selector={(state) => ({
								canSubmit: state.canSubmit,
								isDefaultValue: state.isDefaultValue,
							})}
						>
							{({ canSubmit, isDefaultValue }) => (
								<Button
									type='button'
									onClick={() => {
										setDialogCancel(false);
										open();
									}}
									disabled={!canSubmit || isDefaultValue}
								>
									Save
								</Button>
							)}
						</form.Subscribe>
					</div>
				</div>
			</div>
			<DialogContainer
				close={close}
				className='w-1/2 lg:w-1/3'
				isOpen={isOpen}
				title={dialogCancel ? 'Quit editing?' : 'Save changes?'}
				dataTestId='edit-confirmation-dialog'
			>
				<Typography variant='bodyBig' className='pb-4'>
					{dialogCancel
						? 'Changes you have made so far will not be saved.'
						: 'You have made changes. Do you want to save them?'}
				</Typography>
				<div className='flex justify-between mt-4'>
					<Button variant='tertiary' onClick={close}>
						No, cancel
					</Button>
					<Button
						type='button'
						onClick={() => (dialogCancel ? handleCancel() : form.handleSubmit())}
					>
						Yes, confirm
					</Button>
				</div>
			</DialogContainer>
		</form>
	);
};
